<template>
  <div class="wb-root">

    <!-- ── Header ────────────────────────────────────────────────── -->
    <div class="wb-header">
      <div class="wb-header-left">
        <div class="wb-icon-wrap">
          <feather-icon icon="RadioIcon" size="20" />
        </div>
        <div>
          <h2 class="wb-title">Webinário</h2>
          <p class="wb-sub">Transmissão simulada ao vivo via YouTube</p>
        </div>
      </div>
      <div class="wb-header-right">
        <!-- Schedule badge -->
        <span v-if="scheduledDate && !isStreaming" class="wb-schedule-badge">
          <feather-icon icon="ClockIcon" size="13" />
          <span>Agendado: {{ formatSchedule(scheduledDate) }}</span>
        </span>
        <span class="wb-status" :class="isStreaming ? 'wb-live' : 'wb-offline'">
          <span class="wb-dot"></span>
          {{ isStreaming ? 'AO VIVO' : 'OFFLINE' }}
        </span>
      </div>
    </div>

    <!-- ── Schedule Card (colapsável) ─────────────────────────────── -->
    <div class="wb-schedule-card" v-if="!isStreaming">
      <div class="wb-schedule-header" @click="showSchedule = !showSchedule">
        <div class="wb-schedule-header-left">
          <div class="wb-schedule-icon">
            <feather-icon icon="CalendarIcon" size="18" />
          </div>
          <div>
            <strong class="wb-schedule-title">Agendar Transmissão</strong>
            <p class="wb-schedule-sub">Programe para iniciar automaticamente no horário desejado</p>
          </div>
        </div>
        <feather-icon :icon="showSchedule ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="18" class="wb-schedule-chevron" />
      </div>

      <transition name="wb-slide">
        <div v-if="showSchedule" class="wb-schedule-body">
          <div class="wb-schedule-form">
            <div class="wb-schedule-field">
              <label class="wb-schedule-label">
                <feather-icon icon="CalendarIcon" size="13" />
                Data
              </label>
              <input type="date" class="wb-schedule-input" v-model="scheduleForm.date" :min="todayDate" />
            </div>
            <div class="wb-schedule-field">
              <label class="wb-schedule-label">
                <feather-icon icon="ClockIcon" size="13" />
                Horário
              </label>
              <input type="time" class="wb-schedule-input" v-model="scheduleForm.time" />
            </div>
            <div class="wb-schedule-field">
              <label class="wb-schedule-label">
                <feather-icon icon="GlobeIcon" size="13" />
                Fuso
              </label>
              <select class="wb-schedule-input" v-model="scheduleForm.timezone">
                <option value="America/Sao_Paulo">Brasília (GMT-3)</option>
                <option value="America/Manaus">Manaus (GMT-4)</option>
                <option value="America/Belem">Belém (GMT-3)</option>
                <option value="America/Fortaleza">Fortaleza (GMT-3)</option>
              </select>
            </div>
          </div>
          <div class="wb-schedule-actions">
            <button v-if="scheduledDate" class="wb-btn-ghost wb-btn-sm" @click="cancelSchedule">
              <feather-icon icon="XIcon" size="13" />
              Cancelar agendamento
            </button>
            <button
              class="wb-btn-primary wb-btn-sm"
              :disabled="!scheduleForm.date || !scheduleForm.time"
              @click="saveSchedule"
            >
              <feather-icon icon="CheckIcon" size="13" />
              {{ scheduledDate ? 'Atualizar' : 'Agendar' }}
            </button>
          </div>
          <div v-if="scheduledDate" class="wb-schedule-countdown">
            <feather-icon icon="ZapIcon" size="14" />
            <span>A transmissão iniciará automaticamente em <strong>{{ countdownText }}</strong></span>
          </div>
        </div>
      </transition>
    </div>

    <!-- ── Body ──────────────────────────────────────────────────── -->
    <div class="wb-body">

      <!-- Stepper lateral -->
      <aside class="wb-stepper">
        <div
          v-for="(step, i) in steps"
          :key="step.key"
          class="wb-step"
          :class="{
            'is-active': currentStep === i,
            'is-done':   completedSteps[step.key],
            'is-locked': !canAccess(i),
          }"
          @click="goTo(i)"
        >
          <div class="wb-step-bullet">
            <feather-icon v-if="completedSteps[step.key]" icon="CheckIcon" size="13" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="wb-step-text">
            <span class="wb-step-name">{{ step.label }}</span>
            <span class="wb-step-desc">{{ step.hint }}</span>
          </div>
          <div v-if="i < steps.length - 1" class="wb-step-line"></div>
        </div>
      </aside>

      <!-- Área de conteúdo -->
      <div class="wb-content">

        <!-- Aguardando auth check -->
        <div v-if="loading" class="wb-loading">
          <div class="wb-spinner"></div>
          <span>Verificando autenticação...</span>
        </div>

        <template v-else>
          <!-- Passo 0 — Autenticação -->
          <YouTubeAuth
            v-if="currentStep === 0"
            :is-authenticated="isAuthenticated"
            @auth-success="onAuthSuccess"
          />

          <!-- Passo 1 — Configuração -->
          <StreamConfig
            v-else-if="currentStep === 1"
            :is-authenticated="isAuthenticated"
            :webinario-data="currentWebinario"
            @stream-ready="onStreamReady"
            @config-saved="onConfigSaved"
          />

          <!-- Passo 2 — Chat Agendado -->
          <ChatSimulator
            v-else-if="currentStep === 2"
            :is-streaming="isStreaming"
            :video-url="streamConfig.videoUrl"
            :video-file="streamConfig.videoFile"
            :webinario-data="currentWebinario"
            @chat-saved="onChatSaved"
          />

          <!-- Passo 3 — Triggers -->
          <ChatTriggers
            v-else-if="currentStep === 3"
            :webinario-data="currentWebinario"
            @triggers-saved="onTriggersSaved"
          />

          <!-- Passo 4 — Transmissão -->
          <StreamControls
            v-else-if="currentStep === 4"
            :stream-config="streamConfig"
            :is-authenticated="isAuthenticated"
            @stream-status="onStreamStatus"
          />
        </template>

        <!-- Navegação inferior -->
        <div class="wb-nav" v-if="!loading">
          <button
            class="wb-btn-ghost"
            :disabled="currentStep === 0"
            @click="currentStep--"
          >
            <feather-icon icon="ChevronLeftIcon" size="15" />
            Anterior
          </button>
          <button
            v-if="currentStep < steps.length - 1"
            class="wb-btn-primary"
            @click="goTo(currentStep + 1)"
          >
            Próximo
            <feather-icon icon="ChevronRightIcon" size="15" />
          </button>
          <span v-else class="wb-nav-final">
            <feather-icon icon="RadioIcon" size="14" class="wb-nav-icon" />
            Pronto para transmitir
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'
import YouTubeAuth   from './components/YouTubeAuth.vue'
import StreamConfig  from './components/StreamConfig.vue'
import StreamControls from './components/StreamControls.vue'
import ChatSimulator from './components/ChatSimulator.vue'
import ChatTriggers  from './components/ChatTriggers.vue'

export default {
  name: 'WebinarSimulator',
  components: { YouTubeAuth, StreamConfig, StreamControls, ChatSimulator, ChatTriggers },

  data() {
    const saved = JSON.parse(localStorage.getItem('webinar_schedule') || 'null')
    return {
      currentStep: 0,
      loading: true,
      isAuthenticated: null,
      isStreaming: false,
      currentWebinario: null,
      showSchedule: false,
      scheduledDate: saved ? new Date(saved.datetime) : null,
      scheduleForm: {
        date: saved ? saved.date : '',
        time: saved ? saved.time : '',
        timezone: saved ? saved.timezone : 'America/Sao_Paulo',
      },
      countdownText: '',
      countdownTimer: null,
      streamConfig: {
        streamKey: '',
        videoFile: null,
        videoUrl: '',
        videoKeyName: '',
        quality: '1080p',
        bitrate: 3000,
        fps: 30,
      },
      completedSteps: {
        auth:     false,
        config:   false,
        chat:     false,
        triggers: false,
      },
      steps: [
        { key: 'auth',     label: 'Autenticação',    hint: 'Conectar ao YouTube' },
        { key: 'config',   label: 'Configuração',    hint: 'Vídeo e stream key' },
        { key: 'chat',     label: 'Chat Agendado',   hint: 'Mensagens na timeline' },
        { key: 'triggers', label: 'Triggers',        hint: 'Respostas automáticas' },
        { key: 'stream',   label: 'Transmissão',     hint: 'Iniciar ao vivo' },
      ],
    }
  },

  computed: {
    todayDate() {
      return new Date().toISOString().split('T')[0]
    },
  },

  watch: {
    isAuthenticated(val) {
      if (val) this.completedSteps.auth = true
    },
  },

  mounted() {
    this.init()
    if (this.scheduledDate) this.startCountdown()
  },

  methods: {
    async init() {
      this.loading = true
      await this.checkAuth()
      this.loadConfig()
      await this.loadWebinarios()
      this.loading = false
    },

    async checkAuth() {
      try {
        const userData = localStorage.getItem('userData')
        if (!userData) return
        const userId = JSON.parse(userData).id
        if (!userId) return
        this.isAuthenticated = await youtubeService.checkIntegration(userId)
      } catch {
        this.isAuthenticated = false
      }
    },

    loadConfig() {
      const saved = localStorage.getItem('stream_config')
      if (!saved) return
      const { videoFile, ...rest } = JSON.parse(saved)
      this.streamConfig = { ...this.streamConfig, ...rest }
    },

    async loadWebinarios() {
      try {
        const userData = localStorage.getItem('userData')
        if (!userData) return
        const userId = JSON.parse(userData).id
        const result = await youtubeService.getWebinariosByUser(userId)
        if (result?.data?.length > 0) {
          [this.currentWebinario] = result.data
          this.hydrateFromWebinario(this.currentWebinario)
        }
      } catch { /* silencioso */ }
    },

    hydrateFromWebinario(w) {
      this.streamConfig = {
        ...this.streamConfig,
        streamKey:    w.stream_key      || '',
        videoUrl:     w.video_url       || '',
        videoKeyName: w.video_key_name  || '',
        quality:      w.qualidade       || '1080p',
        bitrate:      parseInt(w.bitrate, 10) || 3000,
        fps:          parseInt(w.fps, 10)     || 30,
      }
      const cfg = {
        title:        w.titulo          || '',
        description:  w.descricao       || '',
        streamKey:    w.stream_key      || '',
        videoUrl:     w.video_url       || '',
        videoKeyName: w.video_key_name  || '',
        quality:      w.qualidade       || '1080p',
        bitrate:      parseInt(w.bitrate, 10) || 3000,
        fps:          parseInt(w.fps, 10)     || 30,
        aiChatEnabled: w.resposta_ia === 1,
        aiKeyword:    w.palavra_chave   || '',
      }
      localStorage.setItem('stream_config', JSON.stringify(cfg))

      if (Array.isArray(w.chat) && w.chat.length > 0) {
        localStorage.setItem('chat_scheduled_messages', JSON.stringify(w.chat))
      }
    },

    canAccess(i) {
      if (i === 0) return true
      if (i === 1) return this.completedSteps.auth
      if (i === 2) return this.completedSteps.config
      if (i === 3) return this.completedSteps.chat
      if (i === 4) return this.completedSteps.triggers
      return false
    },

    goTo(i) {
      if (!this.canAccess(i)) {
        const names = ['autenticação', 'configuração', 'chat agendado', 'triggers']
        this.$toast?.warning?.(`Complete a etapa de ${names[i - 1]} antes de avançar.`)
        return
      }
      this.currentStep = i
    },

    onAuthSuccess()       { this.isAuthenticated = true; this.completedSteps.auth = true; this.goTo(1) },
    onStreamReady(cfg)    { this.streamConfig = { ...this.streamConfig, ...cfg } },
    onConfigSaved()       { this.completedSteps.config = true; this.goTo(2) },
    onChatSaved()         { this.completedSteps.chat = true; this.goTo(3) },
    onTriggersSaved()     { this.completedSteps.triggers = true; this.goTo(4) },
    onStreamStatus(s)     { this.isStreaming = s.isStreaming },

    // ── Schedule methods ──
    saveSchedule() {
      if (!this.scheduleForm.date || !this.scheduleForm.time) return
      const datetime = `${this.scheduleForm.date}T${this.scheduleForm.time}:00`
      this.scheduledDate = new Date(datetime)

      localStorage.setItem('webinar_schedule', JSON.stringify({
        date: this.scheduleForm.date,
        time: this.scheduleForm.time,
        timezone: this.scheduleForm.timezone,
        datetime,
      }))

      this.startCountdown()
      this.$toast?.success?.('Webinário agendado com sucesso!')
    },

    cancelSchedule() {
      this.scheduledDate = null
      this.scheduleForm = { date: '', time: '', timezone: 'America/Sao_Paulo' }
      localStorage.removeItem('webinar_schedule')
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownText = ''
      this.$toast?.info?.('Agendamento cancelado')
    },

    formatSchedule(date) {
      if (!date) return ''
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(date)
    },

    startCountdown() {
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      const tick = () => {
        if (!this.scheduledDate) return
        const diff = this.scheduledDate.getTime() - Date.now()
        if (diff <= 0) {
          this.countdownText = 'Iniciando agora...'
          clearInterval(this.countdownTimer)
          // Auto-start: go to transmission step
          if (this.completedSteps.triggers || this.completedSteps.config) {
            this.goTo(4)
          }
          return
        }
        const d = Math.floor(diff / 86400000)
        const h = Math.floor((diff % 86400000) / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        const s = Math.floor((diff % 60000) / 1000)
        const parts = []
        if (d > 0) parts.push(`${d}d`)
        if (h > 0) parts.push(`${h}h`)
        parts.push(`${m}min`)
        parts.push(`${s}s`)
        this.countdownText = parts.join(' ')
      }
      tick()
      this.countdownTimer = setInterval(tick, 1000)
    },
  },

  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
}
</script>

<style lang="scss" scoped>
/* ─── Root ──────────────────────────────────────────────────────── */
.wb-root {
  min-height: calc(100vh - 80px);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ─── Header ────────────────────────────────────────────────────── */
.wb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  padding: 18px 24px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.wb-header-left { display: flex; align-items: center; gap: 14px; }
.wb-icon-wrap {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0D3349 0%, #1a4f72 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.wb-title  { font-size: 1.25rem; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin: 0; }
.wb-sub    { font-size: 0.78rem; color: var(--dk-text-muted, #8e9aaa); margin: 0; }

.wb-status {
  display: flex; align-items: center; gap: 7px;
  font-size: 0.72rem; font-weight: 800;
  letter-spacing: 0.1em;
  padding: 6px 14px;
  border-radius: 20px;
}
.wb-live    { background: rgba(40,199,111,0.12); color: #28c76f; }
.wb-offline { background: rgba(142,154,170,0.12); color: var(--dk-text-muted, #8e9aaa); }
.wb-dot {
  width: 7px; height: 7px; border-radius: 50%;
  flex-shrink: 0;
}
.wb-live .wb-dot    { background: #28c76f; animation: wb-pulse 1.4s ease infinite; }
.wb-offline .wb-dot { background: #8e9aaa; }
@keyframes wb-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(1.4); }
}

/* ─── Schedule badge (header) ───────────────────────────────────── */
.wb-schedule-badge {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.75rem; font-weight: 600;
  color: #E86A10;
  background: rgba(232,106,16,0.08);
  border: 1px solid rgba(232,106,16,0.2);
  border-radius: 20px;
  padding: 5px 12px;
}

/* ─── Schedule card ────────────────────────────────────────────── */
.wb-schedule-card {
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  border-radius: 14px;
  box-shadow: var(--dk-shadow-sm, 0 2px 8px rgba(0,0,0,0.04));
  overflow: hidden;
}

.wb-schedule-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: var(--dk-bg-card-hover, #fafbfc); }
}
.wb-schedule-header-left { display: flex; align-items: center; gap: 14px; }
.wb-schedule-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(232,106,16,0.1);
  color: #E86A10;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.wb-schedule-title { font-size: 0.92rem; color: var(--dk-text-primary, #0D3349); display: block; }
.wb-schedule-sub   { font-size: 0.75rem; color: var(--dk-text-muted, #8e9aaa); margin: 2px 0 0; }
.wb-schedule-chevron { color: var(--dk-text-muted, #8e9aaa); transition: transform 0.2s; }

.wb-schedule-body {
  padding: 0 20px 20px;
  border-top: 1px solid var(--dk-border-light, #f0f2f5);
}

.wb-schedule-form {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  padding-top: 16px;
}
.wb-schedule-field { display: flex; flex-direction: column; gap: 6px; }
.wb-schedule-label {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--dk-text-muted, #8e9aaa);
}
.wb-schedule-input {
  padding: 9px 12px;
  border: 1.5px solid var(--dk-border-input, #e4e8ef);
  border-radius: 8px;
  font-size: 0.84rem;
  color: var(--dk-text-primary, #0D3349);
  background: var(--dk-bg-input, #f9fafb);
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: #E86A10; }
}

.wb-schedule-actions {
  display: flex; gap: 10px; justify-content: flex-end;
  margin-top: 16px;
}
.wb-btn-sm {
  font-size: 0.78rem !important;
  padding: 7px 14px !important;
}

.wb-schedule-countdown {
  display: flex; align-items: center; gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(40,199,111,0.06);
  border: 1px solid rgba(40,199,111,0.15);
  font-size: 0.8rem;
  color: #28c76f;
  font-weight: 500;
  strong { font-weight: 700; }
}

/* Transition */
.wb-slide-enter-active, .wb-slide-leave-active { transition: all 0.25s ease; overflow: hidden; }
.wb-slide-enter, .wb-slide-leave-to { opacity: 0; max-height: 0; padding-top: 0 !important; padding-bottom: 0 !important; }
.wb-slide-enter-to, .wb-slide-leave { opacity: 1; max-height: 300px; }

@media (max-width: 640px) {
  .wb-schedule-form { grid-template-columns: 1fr; }
}

/* ─── Body ──────────────────────────────────────────────────────── */
.wb-body {
  flex: 1;
  display: flex;
  gap: 20px;
  min-height: 0;
}

/* ─── Stepper ───────────────────────────────────────────────────── */
.wb-stepper {
  width: 220px;
  flex-shrink: 0;
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.wb-step {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;
}
.wb-step:hover:not(.is-locked) { background: var(--dk-bg-inset, #f5f6fa); }

.wb-step-bullet {
  width: 30px; height: 30px;
  border-radius: 50%;
  border: 2px solid var(--dk-border, #d0d7e3);
  background: var(--dk-bg-card, #fff);
  color: var(--dk-text-faint, #aab0bc);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 700;
  flex-shrink: 0;
  transition: all 0.18s;
  z-index: 1;
}
.wb-step.is-active .wb-step-bullet {
  border-color: #E86A10;
  color: #E86A10;
  background: rgba(232,106,16,0.08);
  box-shadow: 0 0 0 3px rgba(232,106,16,0.15);
}
.wb-step.is-done .wb-step-bullet {
  border-color: #28c76f;
  background: #28c76f;
  color: #fff;
}
.wb-step.is-locked { opacity: 0.45; cursor: not-allowed; }

.wb-step-text   { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.wb-step-name   { font-size: 0.82rem; font-weight: 600; color: var(--dk-text-primary, #344563); line-height: 1.2; }
.wb-step.is-active .wb-step-name { color: #E86A10; }
.wb-step.is-done .wb-step-name   { color: #28c76f; }
.wb-step-desc   { font-size: 0.71rem; color: var(--dk-text-faint, #aab0bc); }

/* Linha vertical entre steps */
.wb-step-line {
  position: absolute;
  left: 23px;
  top: 40px;
  width: 2px;
  height: 22px;
  background: var(--dk-border, #e9ecf2);
  z-index: 0;
}
.wb-step.is-done .wb-step-line { background: #28c76f; opacity: 0.5; }

/* ─── Content ───────────────────────────────────────────────────── */
.wb-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ─── Loading ───────────────────────────────────────────────────── */
.wb-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  color: var(--dk-text-muted, #8e9aaa);
  font-size: 0.9rem;
  padding: 60px 0;
}
.wb-spinner {
  width: 22px; height: 22px;
  border-radius: 50%;
  border: 2px solid var(--dk-border, #e4e8ef);
  border-top-color: #E86A10;
  animation: wb-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes wb-spin { to { transform: rotate(360deg); } }

/* ─── Bottom nav ────────────────────────────────────────────────── */
.wb-nav {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}
.wb-btn-ghost {
  display: flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  border: 1.5px solid var(--dk-border, #e4e8ef);
  border-radius: 8px;
  background: var(--dk-bg-card, #fff);
  color: var(--dk-text-secondary, #5a6370);
  font-size: 0.82rem; font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.wb-btn-ghost:hover:not(:disabled) { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }
.wb-btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }

.wb-btn-primary {
  display: flex; align-items: center; gap: 6px;
  padding: 9px 18px;
  border: none;
  border-radius: 8px;
  background: #E86A10;
  color: #fff;
  font-size: 0.82rem; font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.wb-btn-primary:hover { background: #d05e0d; }

.wb-nav-final {
  display: flex; align-items: center; gap: 7px;
  font-size: 0.82rem; font-weight: 600;
  color: #28c76f;
}
.wb-nav-icon { flex-shrink: 0; }

/* ─── Responsive ────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .wb-body { flex-direction: column; }
  .wb-stepper {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px;
  }
  .wb-step { flex: 1 1 120px; padding: 8px; }
  .wb-step-line { display: none; }
}
</style>
